import { formatShortDate } from './dates';
import type { ScheduleEvent } from './types';

/**
 * Nova data de um evento que já existe: o evento que se repete sem data fixa (o culto de
 * batismo, por exemplo). A data entra no mesmo evento — o mesmo card em Eventos, a mesma
 * série no histórico —, com a configuração da última data (título, ministérios e
 * quantidades por função); a escala é montada depois. Só para eventos únicos: a série
 * semanal já tem as datas dela.
 */

export interface OccurrenceBase {
  seriesId: string;
  /** A configuração das novas datas: a última data que não foi alterada sozinha. */
  template: ScheduleEvent;
  /** Quantas datas o evento já tem. */
  count: number;
  /** A data mais recente do evento. */
  last: string;
}

const byDateTime = (a: ScheduleEvent, b: ScheduleEvent) => (a.date + a.time).localeCompare(b.date + b.time);

function datesOf(events: readonly ScheduleEvent[], seriesId: string): ScheduleEvent[] {
  return events.filter((e) => e.seriesId === seriesId).sort(byDateTime);
}

/** A configuração das novas datas, ou `null` se o evento não existe ou é semanal. */
export function occurrenceTemplate(events: readonly ScheduleEvent[], seriesId: string): ScheduleEvent | null {
  const dates = datesOf(events, seriesId);
  const template = dates.filter((e) => !e.edited).at(-1) ?? dates.at(-1) ?? null;

  return template && !template.recurrence ? template : null;
}

/** Os eventos únicos que recebem nova data, passados e futuros, do mais recente ao mais antigo. */
export function occurrenceBases(events: readonly ScheduleEvent[]): OccurrenceBase[] {
  const seriesIds = [...new Set(events.filter((e) => !e.recurrence).map((e) => e.seriesId))];

  return seriesIds
    .flatMap((seriesId): OccurrenceBase[] => {
      const template = occurrenceTemplate(events, seriesId);
      const dates = datesOf(events, seriesId);

      return template ? [{ seriesId, template, count: dates.length, last: dates.at(-1)!.date }] : [];
    })
    .sort((a, b) => b.last.localeCompare(a.last));
}

/** "Culto de Batismo · última em Dom, 13 set" (ou "· 3 datas, a última em …"). */
export function occurrenceBaseLabel(base: OccurrenceBase): string {
  const when = formatShortDate(base.last);

  return base.count > 1
    ? `${base.template.title} · ${base.count} datas, a última em ${when}`
    : `${base.template.title} · ${when}`;
}

/** Por que a data não pode entrar no evento, ou `null`. */
export function occurrenceError(
  events: readonly ScheduleEvent[],
  seriesId: string,
  date: string
): string | null {
  if (!events.some((e) => e.seriesId === seriesId)) return 'Este evento não existe mais.';
  if (!occurrenceTemplate(events, seriesId))
    return 'O evento semanal já tem as datas dele: edite a série ou crie um evento novo.';
  if (!date) return 'Escolha a data.';
  if (events.some((e) => e.seriesId === seriesId && e.date === date))
    return `Este evento já tem uma data em ${formatShortDate(date)}.`;

  return null;
}

/** Acrescenta a data ao evento, com a configuração da última data e o horário escolhido. */
export function addOccurrence(
  events: ScheduleEvent[],
  seriesId: string,
  date: string,
  time: string
): { events: ScheduleEvent[]; created: ScheduleEvent | null } {
  const template = occurrenceTemplate(events, seriesId);

  if (!template || occurrenceError(events, seriesId, date)) return { events, created: null };

  const ids = new Set(events.map((e) => e.id));
  let id = `${seriesId}-${date}`;

  for (let n = 2; ids.has(id); n++) id = `${seriesId}-${date}-${n}`;

  const created: ScheduleEvent = {
    id,
    seriesId,
    title: template.title,
    date,
    time: time || template.time,
    recurrence: null,
    rule: null,
    color: template.color,
    ministryIds: [...template.ministryIds],
    need: { ...template.need },
    slots: {},
    generated: false,
    published: false,
    edited: false,
    responses: {},
    attendance: {},
    nudgedAt: null,
    recorded: false,
    notices: {},
    swaps: [],
    audience: null,
  };

  return { events: [...events, created].sort(byDateTime), created };
}
